// MyAccessCard — Home "My access" donut + legend.
// design ref: home.jsx MyAccessCard + styles.css:668–703 (.donut family).
// Buckets the member's active access by time remaining: stable (>30d or
// indefinite), expiring <30d, expiring <7d. The three counts are disjoint and
// sum to the donut total.

import { Box, HStack } from "@chakra-ui/react";
import { TbLayoutGrid } from "react-icons/tb";

import Card from "components/ds/Card";
import CardHeader from "components/ds/CardHeader";
import EmptyState from "components/ds/EmptyState";
import PloyButton from "components/ds/PloyButton";
import type { MFSelectableResourceAccess } from "state/members/access_v2/catalogs";
import { accessMetrics } from "../MyAccess/AccessGroup";
import Donut from "./Donut";

interface LegendRowProps {
  color: string; // theme token for the pill dot
  label: string;
  sub?: string;
  count: number;
}

// design ref: styles.css:692–703 + home.jsx inline pill dots (12×8, fully round)
const LegendRow = ({ color, label, sub, count }: LegendRowProps) => (
  <HStack spacing="sm" fontSize="md" align="center">
    <Box w="12px" h="8px" borderRadius="full" bg={color} flexShrink={0} />
    <Box flex="1" color="neutral.900" fontWeight={500}>
      {label}
    </Box>
    {sub && (
      <Box color="neutral.700" fontWeight={500}>
        {sub}
      </Box>
    )}
    <Box fontWeight={700} color="neutral.950">
      {count}
    </Box>
  </HStack>
);

export interface MyAccessCardProps {
  access: MFSelectableResourceAccess[];
  onViewAll: () => void;
  /** Omitted when the org took Catalog out of its portal nav (PLO-5580). */
  onBrowseCatalog?: () => void;
}

const MyAccessCard = ({
  access,
  onViewAll,
  onBrowseCatalog,
}: MyAccessCardProps) => {
  let stable = 0;
  let warning = 0;
  let critical = 0;
  for (const a of access) {
    const m = accessMetrics(a);
    if (m.indefinite || m.daysLeft > 30) stable += 1;
    else if (m.daysLeft <= 7) critical += 1;
    else warning += 1;
  }
  const total = access.length;

  return (
    <Card>
      <CardHeader title="My access" action={{ label: "View all", onClick: onViewAll }} />

      {total === 0 ? (
        <EmptyState
          icon={<TbLayoutGrid />}
          title="No active access yet"
          body={
            onBrowseCatalog
              ? "Browse the catalog to request access to apps and resources."
              : "Access granted to you will show up here."
          }
          action={
            onBrowseCatalog && (
              <PloyButton variant="azurite-link" onClick={onBrowseCatalog}>
                Browse the catalog →
              </PloyButton>
            )
          }
        />
      ) : (
        // design ref: styles.css:668–670 — .donut (flex, gap 24px)
        <HStack spacing="1xl" align="center">
          <Donut
            total={total}
            segments={[
              { value: stable, color: "var(--chakra-colors-success-500)" },
              { value: warning, color: "var(--chakra-colors-warning-500)" },
              { value: critical, color: "var(--chakra-colors-error-500)" },
            ]}
          />
          {/* design ref: styles.css:688–691 — .donut__legend */}
          <Box flex="1" display="flex" flexDirection="column" gap="sm">
            <LegendRow color="success.500" label="Stable" count={stable} />
            <LegendRow
              color="warning.500"
              label="Expiring soon"
              sub="(<30 days)"
              count={warning}
            />
            <LegendRow
              color="error.500"
              label="Expiring soon"
              sub="(<7 days)"
              count={critical}
            />
          </Box>
        </HStack>
      )}
    </Card>
  );
};

export default MyAccessCard;
